import React, { useEffect, useState } from 'react';
import { 
  Container, 
  Icon, 
  Loader,
  Modal,
} from 'semantic-ui-react';
import styled from 'styled-components';
import { getLinkedVendors, initiateVendorLink } from "./energy-device-oems.client" 
import { Toast } from '../../../ui/Toast';
import { SungrowLoginForm } from './sungrow/australia/SungrowLoginForm';
import { usePopup } from '../../../ui/popup/Popup';
import { useInstantUpdater } from '../../../generic.hooks';
import { Vendor, VENDOR_LIST } from './constants';

// Premium styled components
const PageContainer = styled.div`
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  min-height: 100vh;
  padding: 2rem 0;
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  
  .main-title {
    font-size: 2.5rem;
    font-weight: 700;
    background: linear-gradient(135deg, #6435C9 0%, #480971 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    margin-bottom: 1rem;
  }
  
  .subtitle {
    font-size: 1.1rem;
    color: #64748b;
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.6;
  }
`;

const VendorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 3rem;
`;

const VendorCard = styled.div<{ color?: string; isLinked?: boolean; integrationStatus: Vendor['integrationStatus'] }>`
  background: white;
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 2px solid ${props => props.isLinked ? '#10b981' : 'transparent'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  cursor: ${props => props.integrationStatus !== 'not-available' && !props.isLinked ? 'pointer' : 'default'};
  min-height: 200px;
  display: flex;
  flex-direction: column;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.color || '#6435C9'};
    opacity: ${props => props.isLinked ? '0.7' : '0.3'};
  }
  
  &:hover {
    transform: ${props => props.integrationStatus !== 'not-available' ? 'translateY(-8px)' : 'none'};
    box-shadow: ${props => props.integrationStatus !== 'not-available' ? '0 20px 40px rgba(0, 0, 0, 0.12)' : '0 4px 20px rgba(0, 0, 0, 0.08)'};
  }
  
  ${props => props.isLinked && `
    background: linear-gradient(135deg, #f0fdf4 0%, #ffffff 100%);
    border-color: #10b981;
    
    &::before {
      background: ${props.color || '#6435C9'};
      opacity: 0.8;
    }
  `}
  
  ${props => props.integrationStatus === 'not-available' && `
    opacity: 0.6;
    filter: grayscale(0.3);
  `}
`;

const StatusBadge = styled.div<{ isLinked: boolean }>`
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  background: ${props => props.isLinked ? 'linear-gradient(135deg, #10b981, #059669)' : '#f1f5f9'};
  color: ${props => props.isLinked ? 'white' : '#64748b'};
  
  &::before {
    content: '${props => props.isLinked ? '✓ Linked' : '○ Unlinked'}';
    font-weight: bold;
  }
`;

const VendorBadge = styled.div`
  position: absolute;
  top: 1.5rem;
  left: 1.5rem;
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
  z-index: 5;
`;

const VendorHeader = styled.div<{ hasBadge?: boolean }>`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: auto;
  padding-top: ${props => props.hasBadge ? '2.5rem' : '0'};
  flex: 1;
`;

const VendorLogo = styled.div<{ color?: string }>`
  width: 64px;
  height: 64px;
  border-radius: 18px;
  background: 'transparent';
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: '0 4px 12px rgba(0, 0, 0, 0.1), inset 0 0 0 1px rgba(0, 0, 0, 0.05)';
  font-size: 1.75rem;
  flex-shrink: 0;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const VendorInfo = styled.div`
  flex: 1;
  
  .vendor-name {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 0.5rem;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: auto;
`;

const ActionButton = styled.button<{ variant: 'primary' | 'secondary' | 'danger'; color?: string }>`
  flex: 1;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  ${props => props.variant === 'primary' && `
    background: ${props.color || '#6435C9'};
    color: white;
    
    &:hover {
      background: ${props.color ? props.color + 'dd' : '#5b21b6'};
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(100, 53, 201, 0.3);
    }
  `}
  
  ${props => props.variant === 'secondary' && `
    background: #f1f5f9;
    color: #475569;
    border: 1px solid #e2e8f0;
    
    &:hover {
      background: #e2e8f0;
      transform: translateY(-2px);
    }
  `}
  
  ${props => props.variant === 'danger' && `
    background: #fef2f2;
    color: #dc2626;
    border: 1px solid #fecaca;
    
    &:hover {
      background: #fee2e2;
      transform: translateY(-2px);
    }
  `}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
  }
`;

const SupportSection = styled.div`
  background: white;
  border-radius: 20px;
  padding: 2rem;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
  
  .support-title {
    font-size: 1.25rem;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 0.5rem;
  }
  
  .support-text {
    color: #64748b;
    margin-bottom: 1.5rem;
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 20px;
  z-index: 10;
`;


export function EnergyDeviceVendorsIntegration({ companyId, renderHeader = true }: { companyId: string, renderHeader?: boolean }) {
  const [linkedVendors, setLinkedVendors] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [linkingVendor, setLinkingVendor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { openPopup, closePopup } = usePopup();
  let [id, update] = useInstantUpdater();

  useEffect(() => {
    loadVendorStatuses();
  }, [companyId, id]);

  const loadVendorStatuses = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await getLinkedVendors(companyId);
      setLinkedVendors(response);         
    } catch (err) {
      setError('Failed to load vendor connection statuses');
      console.error('Error loading vendor statuses:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleVendorClick = async (vendor: Vendor) => {
    if (vendor.integrationStatus === 'not-available') return;
    
    const isLinked = linkedVendors.includes(vendor.id);
    
    if (isLinked) {
      return;
    }
    
    await handleLinkVendor(vendor.id);
  };

  const handleLinkVendor = async (vendorId: string) => {
    setLinkingVendor(vendorId);
    setError(null);

    if(vendorId === 'SUNGROW-AUSTRALIA'){
      openPopup(<Modal
      size={"mini"}
      open={true}
      closeIcon
      closeOnEscape={false}
      closeOnDimmerClick={false}
      onClose={()=>{
        setLinkingVendor(null);
        closePopup();
      }}
      >
                <SungrowLoginForm
         companyId={companyId}
         onLoginInit={()=>{
          setLinkingVendor(vendorId);
          setError(null);
        }}
          onSuccessfullLogin={()=>{
            setLinkingVendor(null);
            closePopup();
            // setShowSungrowLoginModal(false);
            update();
          }} 
          onFailedLogin={(errorMsg?: string | null)=>{
            if(errorMsg !== undefined) {
              setError(errorMsg);
            }
            setLinkingVendor(null);
            closePopup();
            // setShowSungrowLoginModal(false);
          }}
      />
        </Modal>
)
    } else {
      try {
        const { linkUrl } = await initiateVendorLink(companyId, vendorId);
        // console.info("Setting in sessionStorage");
        // sessionStorage.setItem('oauth2_redirectUri', window.location.pathname);
        window.location.href = linkUrl;
      } catch (err) {
        setError(`Failed to initiate link with ${VENDOR_LIST.find((v)=> v.id===vendorId)?.name}`);
        console.error('Error linking vendor:', err);
      } finally {
        setLinkingVendor(null);
      }
    }
  };

  // if (loading) {
  //   return <LoadingBlock />;
  // }

  return (
    <PageContainer>
      <Container fluid>
        {renderHeader && <HeaderSection>
          <h1 className="main-title">Energy Device Integrations</h1>
          <p className="subtitle">
            Connect your solar inverters to automatically sync data and monitor performance.
          </p>
        </HeaderSection>}

        <Toast onClose={() => setError(null)} severity="error" content={error} />

        <VendorGrid>
          {VENDOR_LIST.filter((vendor) => vendor.id !== 'SUNGROW-AUSTRALIA').map((vendor) => {
            const isLinked = linkedVendors.includes(vendor.id);
            const isLoading = loading || linkingVendor === vendor.id;
            /* || 
                            disconnectingVendor === vendor.id || 
                            syncingVendor === vendor.id; */

            return (
              <VendorCard
                key={vendor.id}
                color={vendor.color}
                isLinked={isLinked}
                integrationStatus={vendor.integrationStatus}
                onClick={() => handleVendorClick(vendor)}
              >
                {isLoading && (
                  <LoadingOverlay>
                    <Loader size="small" active inline />
                  </LoadingOverlay>
                )}
                
                {vendor.integrationStatus !== 'available' && <VendorBadge>{vendor.integrationStatus}</VendorBadge>}
                <StatusBadge isLinked={isLinked || false}>
                  {/* {isLinked ? 'Connected' : ''} */}
                </StatusBadge>
                
                <VendorHeader hasBadge={vendor.integrationStatus !== 'available'}>
                  <VendorLogo 
                    color={vendor.color} 
                  >
                    <img src={vendor.logo} alt={`${vendor.name} logo`} />
                  </VendorLogo>
                  <VendorInfo>
                    <div className="vendor-name">{vendor.name}</div>
                  </VendorInfo>
                </VendorHeader>
                <ActionButtons>
                  {isLinked ? (
                    <>
                      {/* <ActionButton
                        variant="secondary"
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          handleSyncVendor(vendor.id);
                        }}
                        disabled={isLoading}
                      >
                        <Icon name="refresh" />
                        Sync
                      </ActionButton>
                      <ActionButton
                        variant="danger"
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          handleDisconnectVendor(vendor.id);
                        }}
                        disabled={isLoading}
                      >
                        <Icon name="unlink" />
                        Disconnect
                      </ActionButton> */}
                    </>
                  ) : (
                    <ActionButton
                      variant="primary"
                      color={vendor.color}
                      disabled={vendor.integrationStatus === 'not-available' || isLoading}
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        handleLinkVendor(vendor.id);
                      }}
                    >
                      <Icon name="linkify" />
                      {vendor.integrationStatus !== 'not-available' ? 'Connect Account' : 'Coming Soon'}
                    </ActionButton>
                  )}
                </ActionButtons>
              </VendorCard>
            );
          })}
        </VendorGrid>

        <SupportSection>
          <div className="support-title">Need help connecting your devices?</div>
          <div className="support-text">
            Our expert support team is here to help you integrate your energy devices seamlessly. 
            Get personalized assistance for any vendor connections.
          </div>
          <ActionButton 
            variant="secondary"
            onClick={() => {
              const mailtoLink = `mailto:team@perlstreet.com?subject=Support needed to integrate energy devices`;
              window.open(mailtoLink, '_blank');
            }}
            style={{ margin: '0 auto', display: 'flex' }}
          >
            <Icon name="help" />
            Get Support
          </ActionButton>
        </SupportSection>
      </Container>
    </PageContainer>
  );
}